import { TRAVELER_QUESTIONS, type TravelerQuestionAnswer } from "./questionBank";
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase/client";
import { loadCommunityReviewInsights } from "../profile/worldCommunityService";

export const QUIZ_AXES = [
  "seaMountain",
  "cityNature",
  "warmCold",
  "plannedAdventure",
  "luxuryBudget",
  "relaxActivity",
  "cultureNightlife",
  "shortLong",
  "popularRemote",
  "comfortAuthentic",
  "foodCulture",
  "earlyLate",
  "soloSocial",
  "fixedFlexible",
  "directMultiStop",
  "classicUnusual"
] as const;
export type QuizAxis = typeof QUIZ_AXES[number];

export interface QuizAnswer { axis: QuizAxis; choice: -1 | 1; answeredAt: string; }
export interface TravelerQuizProfile { version: 2; answers: QuizAnswer[]; questionAnswers?: TravelerQuestionAnswer[]; updatedAt: string; }
export interface QuizCommunityAxisSummary { axis: QuizAxis; responses: number; averageChoice: number; minusPercent: number; plusPercent: number; }
export interface QuizCommunitySummary { users: number; responses: number; axes: QuizCommunityAxisSummary[]; }

const emptyProfile = (): TravelerQuizProfile => ({ version: 2, answers: [], updatedAt: new Date().toISOString() });
const localKey = (uid: string) => `tripai.travelerQuiz.v2.${uid}`;
const legacyLocalKey = (uid: string) => `tripai.travelerQuiz.v1.${uid}`;
let communitySummaryCache: { expiresAt: number; value: QuizCommunitySummary } | null = null;

function validChoice(value: unknown): value is -1 | 1 { return value === -1 || value === 1; }
function normalize(value: unknown): TravelerQuizProfile {
  if (!value || typeof value !== "object") return emptyProfile();
  const raw = value as { answers?: unknown[]; questionAnswers?: unknown[]; updatedAt?: unknown };
  const byAxis = new Map<QuizAxis, QuizAnswer>();
  for (const item of Array.isArray(raw.answers) ? raw.answers : []) {
    if (!item || typeof item !== "object") continue;
    const answer = item as Partial<QuizAnswer>;
    if (!QUIZ_AXES.includes(answer.axis as QuizAxis) || !validChoice(answer.choice)) continue;
    byAxis.set(answer.axis as QuizAxis, {
      axis: answer.axis as QuizAxis,
      choice: answer.choice,
      answeredAt: typeof answer.answeredAt === "string" ? answer.answeredAt : new Date().toISOString()
    });
  }
  const questionById = new Map(TRAVELER_QUESTIONS.map(question => [question.id, question]));
  const details = new Map<string, TravelerQuestionAnswer>();
  for (const item of Array.isArray(raw.questionAnswers) ? raw.questionAnswers : []) {
    if (!item || typeof item !== "object") continue;
    const answer = item as Partial<TravelerQuestionAnswer>;
    const question = questionById.get(String(answer.questionId || ""));
    if (!question || answer.axis !== question.axis || !validChoice(answer.choice)) continue;
    const clean = { questionId: question.id, axis: question.axis, choice: answer.choice, answeredAt: typeof answer.answeredAt === "string" ? answer.answeredAt : new Date().toISOString() };
    const existing = details.get(question.id);
    if (!existing || clean.answeredAt >= existing.answeredAt) details.set(question.id, clean);
  }
  // Original saved answers also complete their corresponding original questions.
  for (const answer of byAxis.values()) if (!details.has(answer.axis)) details.set(answer.axis, { ...answer, questionId: answer.axis });
  for (const axis of QUIZ_AXES) {
    const evidence = [...details.values()].filter(answer => answer.axis === axis);
    if (!evidence.length) continue;
    const sum = evidence.reduce((total, answer) => total + answer.choice, 0);
    const latest = evidence.reduce((a, b) => a.answeredAt > b.answeredAt ? a : b);
    byAxis.set(axis, { axis, choice: sum === 0 ? latest.choice : sum > 0 ? 1 : -1, answeredAt: latest.answeredAt });
  }
  return {
    version: 2,
    answers: QUIZ_AXES.flatMap((axis) => byAxis.get(axis) || []),
    questionAnswers: TRAVELER_QUESTIONS.flatMap(question => details.get(question.id) || []),
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : new Date().toISOString()
  };
}

export function loadLocalQuiz(uid: string): TravelerQuizProfile {
  try {
    const current = localStorage.getItem(localKey(uid));
    if (current) return normalize(JSON.parse(current));
    const legacy = localStorage.getItem(legacyLocalKey(uid));
    const migrated = normalize(JSON.parse(legacy || "null"));
    if (legacy) localStorage.setItem(localKey(uid), JSON.stringify(migrated));
    return migrated;
  } catch {
    return emptyProfile();
  }
}

// Cloud wins conflicts; answers present only on this device survive a read.
// Reading never uploads local data. The existing explicit save remains the writer.
function mergeCloudAndLocalQuiz(cloud: TravelerQuizProfile, local: TravelerQuizProfile): TravelerQuizProfile {
  return normalize({
    version: 2,
    answers: [...local.answers, ...cloud.answers],
    questionAnswers: [
      ...(local.questionAnswers || []).filter(answer => !(cloud.questionAnswers || []).some(remote => remote.questionId === answer.questionId)),
      ...(cloud.questionAnswers || [])
    ],
    updatedAt: cloud.updatedAt
  });
}

export async function loadTravelerQuizProfile(uid: string): Promise<TravelerQuizProfile> {
  try {
    const snapshot = await getDoc(doc(db, "users", uid, "travelerQuiz", "profile"));
    const local = loadLocalQuiz(uid);
    const profile = snapshot.exists() ? mergeCloudAndLocalQuiz(normalize(snapshot.data()), local) : local;
    localStorage.setItem(localKey(uid), JSON.stringify(profile));
    return profile;
  } catch {
    return loadLocalQuiz(uid);
  }
}

export function subscribeTravelerQuiz(uid: string, onChange: (profile: TravelerQuizProfile) => void, onError: () => void): () => void {
  onChange(loadLocalQuiz(uid));
  return onSnapshot(doc(db, "users", uid, "travelerQuiz", "profile"), (snapshot) => {
    const local = loadLocalQuiz(uid);
    const profile = snapshot.exists() ? mergeCloudAndLocalQuiz(normalize(snapshot.data()), local) : local;
    localStorage.setItem(localKey(uid), JSON.stringify(profile));
    onChange(profile);
  }, onError);
}

export async function saveTravelerQuiz(uid: string, profile: TravelerQuizProfile): Promise<TravelerQuizProfile> {
  let clean = normalize({ ...profile, updatedAt: new Date().toISOString() });
  if (clean.answers.length !== profile.answers.length || (profile.questionAnswers && clean.questionAnswers?.length !== profile.questionAnswers.length)) throw new Error("QUIZ_INVALID_DATA");
  const reference = doc(db, "users", uid, "travelerQuiz", "profile");
  await runTransaction(db, async transaction => {
    const snapshot = await transaction.get(reference);
    const stored = normalize(snapshot.exists() ? snapshot.data() : null);
    clean = normalize({ ...clean, answers: [...stored.answers, ...clean.answers], questionAnswers: [...(stored.questionAnswers || []), ...(clean.questionAnswers || [])] });
    transaction.set(reference, { ...clean, serverUpdatedAt: serverTimestamp() }, { merge: false });
  });
  localStorage.setItem(localKey(uid), JSON.stringify(clean));

  // Community copy stores quiz choices plus a separate gamification namespace.
  // It is best-effort so the private quiz continues to work even if community features are temporarily unavailable.
  try {
    await setDoc(doc(db, "travelerQuizCommunity", uid), {
      version: 2,
      answers: clean.answers.map(({ axis, choice }) => ({ axis, choice })),
      answerCount: clean.answers.length,
      updatedAt: clean.updatedAt,
      serverUpdatedAt: serverTimestamp()
    }, { merge: true });
    communitySummaryCache = null;
  } catch {
    // Private profile remains the source of truth.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("tripai:traveler-quiz-updated", { detail: { uid } }));
    window.dispatchEvent(new CustomEvent("tripai:gamification-source-updated", { detail: { uid } }));
  }
  return clean;
}

export { normalize as normalizeTravelerQuizProfile };

export async function loadCommunityQuizSummary(maxUsers = 250): Promise<QuizCommunitySummary> {
  if (communitySummaryCache && communitySummaryCache.expiresAt > Date.now()) return communitySummaryCache.value;
  const accum = new Map<QuizAxis, { minus: number; plus: number }>();
  QUIZ_AXES.forEach((axis) => accum.set(axis, { minus: 0, plus: 0 }));
  let users = 0;
  let responses = 0;
  try {
    const snapshot = await getDocs(query(collection(db, "travelerQuizCommunity"), limit(Math.max(1, Math.min(500, maxUsers)))));
    snapshot.docs.forEach((entry) => {
      const profile = normalize(entry.data());
      if (!profile.answers.length) return;
      users += 1;
      profile.answers.forEach((answer) => {
        const bucket = accum.get(answer.axis);
        if (!bucket) return;
        if (answer.choice === -1) bucket.minus += 1; else bucket.plus += 1;
        responses += 1;
      });
    });
  } catch {
    return { users: 0, responses: 0, axes: [] };
  }

  const value: QuizCommunitySummary = {
    users,
    responses,
    axes: QUIZ_AXES.flatMap((axis) => {
      const bucket = accum.get(axis)!;
      const total = bucket.minus + bucket.plus;
      if (!total) return [];
      return [{
        axis,
        responses: total,
        averageChoice: Number(((bucket.plus - bucket.minus) / total).toFixed(3)),
        minusPercent: Math.round(bucket.minus / total * 100),
        plusPercent: Math.round(bucket.plus / total * 100)
      }];
    })
  };
  communitySummaryCache = { expiresAt: Date.now() + 5 * 60 * 1000, value };
  return value;
}

export interface DestinationMatch {
  id: string;
  score: number;
  countryCodes: string[];
  label?: string;
}

export interface DestinationRankingOptions {
  limit?: number;
  excludeCountryCodes?: string[];
}

type Destination = {
  id: string;
  countryCodes: string[];
  label?: string;
  weights: Partial<Record<QuizAxis, number>>;
};

const DEFAULT_DESTINATIONS: Destination[] = [
  { id: "bali", countryCodes:["ID"], weights: { seaMountain:-.7, cityNature:.7, warmCold:-1, plannedAdventure:.6, luxuryBudget:.2, relaxActivity:-.3, cultureNightlife:-.5, shortLong:.8, popularRemote:.1, comfortAuthentic:.6, foodCulture:-.2, earlyLate:-.2, soloSocial:.2, fixedFlexible:.7, directMultiStop:.6, classicUnusual:.3 } },
  { id: "tokyo", countryCodes:["JP"], weights: { seaMountain:0, cityNature:-1, warmCold:0, plannedAdventure:-.3, luxuryBudget:.4, relaxActivity:.7, cultureNightlife:-.4, shortLong:.7, popularRemote:-.8, comfortAuthentic:-.1, foodCulture:-.2, earlyLate:.4, soloSocial:.1, fixedFlexible:-.2, directMultiStop:.2, classicUnusual:.1 } },
  { id: "iceland", countryCodes:["IS"], weights: { seaMountain:.8, cityNature:1, warmCold:1, plannedAdventure:.8, luxuryBudget:.1, relaxActivity:.8, cultureNightlife:.5, shortLong:.6, popularRemote:.8, comfortAuthentic:.5, foodCulture:.7, earlyLate:-.6, soloSocial:-.1, fixedFlexible:.1, directMultiStop:.7, classicUnusual:.7 } },
  { id: "maldives", countryCodes:["MV"], weights: { seaMountain:-1, cityNature:.8, warmCold:-1, plannedAdventure:-.5, luxuryBudget:-1, relaxActivity:-1, cultureNightlife:.8, shortLong:-.2, popularRemote:.4, comfortAuthentic:-.8, foodCulture:.4, earlyLate:-.6, soloSocial:.3, fixedFlexible:-.7, directMultiStop:-.4, classicUnusual:.1 } },
  { id: "newYork", countryCodes:["US"], weights: { seaMountain:0, cityNature:-1, warmCold:0, plannedAdventure:.2, luxuryBudget:.2, relaxActivity:1, cultureNightlife:.6, shortLong:.2, popularRemote:-1, comfortAuthentic:-.5, foodCulture:-.1, earlyLate:.8, soloSocial:.6, fixedFlexible:.1, directMultiStop:-.5, classicUnusual:-.5 } },
  { id: "dolomites", countryCodes:["IT"], weights: { seaMountain:1, cityNature:1, warmCold:.7, plannedAdventure:.4, luxuryBudget:.1, relaxActivity:.7, cultureNightlife:.6, shortLong:-.2, popularRemote:.4, comfortAuthentic:.5, foodCulture:.4, earlyLate:-.9, soloSocial:-.2, fixedFlexible:.3, directMultiStop:.2, classicUnusual:.2 } },
  { id: "barcelona", countryCodes:["ES"], weights: { seaMountain:-.5, cityNature:-.8, warmCold:-.8, plannedAdventure:.3, luxuryBudget:.4, relaxActivity:.2, cultureNightlife:.8, shortLong:-.6, popularRemote:-.9, comfortAuthentic:.1, foodCulture:-.4, earlyLate:.8, soloSocial:.8, fixedFlexible:.5, directMultiStop:-.5, classicUnusual:-.4 } },
  { id: "costarica", countryCodes:["CR"], weights: { seaMountain:-.4, cityNature:1, warmCold:-1, plannedAdventure:1, luxuryBudget:.5, relaxActivity:.8, cultureNightlife:.2, shortLong:.7, popularRemote:.7, comfortAuthentic:.8, foodCulture:.4, earlyLate:-.7, soloSocial:-.1, fixedFlexible:.8, directMultiStop:.7, classicUnusual:.7 } },
  { id: "paris", countryCodes:["FR"], weights: { seaMountain:0, cityNature:-1, warmCold:0, plannedAdventure:-.5, luxuryBudget:-.6, relaxActivity:.1, cultureNightlife:-.8, shortLong:-.4, popularRemote:-1, comfortAuthentic:-.6, foodCulture:-.6, earlyLate:.2, soloSocial:.1, fixedFlexible:-.4, directMultiStop:-.7, classicUnusual:-.8 } },
  { id: "thailand", countryCodes:["TH"], weights: { seaMountain:-.8, cityNature:.6, warmCold:-1, plannedAdventure:.7, luxuryBudget:.9, relaxActivity:.1, cultureNightlife:.2, shortLong:.8, popularRemote:.2, comfortAuthentic:.9, foodCulture:-.5, earlyLate:.1, soloSocial:.6, fixedFlexible:.8, directMultiStop:.8, classicUnusual:.5 } },
  { id: "norway", countryCodes:["NO"], weights: { seaMountain:.8, cityNature:1, warmCold:1, plannedAdventure:.5, luxuryBudget:-.2, relaxActivity:.7, cultureNightlife:.5, shortLong:.5, popularRemote:.7, comfortAuthentic:.3, foodCulture:.6, earlyLate:-.7, soloSocial:-.2, fixedFlexible:.2, directMultiStop:.7, classicUnusual:.5 } },
  { id: "dubai", countryCodes:["AE"], weights: { seaMountain:-.5, cityNature:-1, warmCold:-1, plannedAdventure:-.7, luxuryBudget:-1, relaxActivity:-.4, cultureNightlife:.6, shortLong:-.4, popularRemote:-.9, comfortAuthentic:-1, foodCulture:.2, earlyLate:.4, soloSocial:.4, fixedFlexible:-.5, directMultiStop:-.8, classicUnusual:-.1 } },
  { id: "lisbon", countryCodes:["PT"], weights: { seaMountain:-.4, cityNature:-.7, warmCold:-.7, plannedAdventure:.1, luxuryBudget:.5, relaxActivity:-.1, cultureNightlife:-.2, shortLong:-.5, popularRemote:-.5, comfortAuthentic:.4, foodCulture:-.5, earlyLate:.4, soloSocial:.4, fixedFlexible:.5, directMultiStop:-.4, classicUnusual:-.2 } },
  { id: "rome", countryCodes:["IT"], weights: { seaMountain:0, cityNature:-1, warmCold:-.4, plannedAdventure:-.2, luxuryBudget:.1, relaxActivity:.3, cultureNightlife:-.9, shortLong:-.5, popularRemote:-.8, comfortAuthentic:-.1, foodCulture:-.9, earlyLate:.1, soloSocial:.3, fixedFlexible:.1, directMultiStop:-.6, classicUnusual:-.8 } },
  { id: "london", countryCodes:["GB"], weights: { seaMountain:0, cityNature:-1, warmCold:.4, plannedAdventure:-.2, luxuryBudget:-.2, relaxActivity:.6, cultureNightlife:-.6, shortLong:-.4, popularRemote:-.9, comfortAuthentic:-.4, foodCulture:-.3, earlyLate:.7, soloSocial:.5, fixedFlexible:.1, directMultiStop:-.7, classicUnusual:-.6 } },
  { id: "athens", countryCodes:["GR"], weights: { seaMountain:-.4, cityNature:-.8, warmCold:-.8, plannedAdventure:.1, luxuryBudget:.4, relaxActivity:.1, cultureNightlife:-.9, shortLong:-.4, popularRemote:-.6, comfortAuthentic:.3, foodCulture:-.8, earlyLate:.2, soloSocial:.4, fixedFlexible:.4, directMultiStop:-.5, classicUnusual:-.5 } },
  { id: "istanbul", countryCodes:["TR"], weights: { seaMountain:-.2, cityNature:-.9, warmCold:-.4, plannedAdventure:.3, luxuryBudget:.5, relaxActivity:.4, cultureNightlife:-.8, shortLong:-.2, popularRemote:-.5, comfortAuthentic:.7, foodCulture:-.8, earlyLate:.5, soloSocial:.6, fixedFlexible:.6, directMultiStop:-.3, classicUnusual:.1 } },
  { id: "marrakech", countryCodes:["MA"], weights: { seaMountain:.1, cityNature:-.7, warmCold:-1, plannedAdventure:.5, luxuryBudget:.3, relaxActivity:.2, cultureNightlife:-.7, shortLong:-.3, popularRemote:-.3, comfortAuthentic:.9, foodCulture:-.7, earlyLate:.2, soloSocial:.3, fixedFlexible:.7, directMultiStop:-.1, classicUnusual:.5 } },
  { id: "capeTown", countryCodes:["ZA"], weights: { seaMountain:.3, cityNature:.4, warmCold:-.6, plannedAdventure:.7, luxuryBudget:.2, relaxActivity:.5, cultureNightlife:.1, shortLong:.7, popularRemote:.2, comfortAuthentic:.5, foodCulture:-.2, earlyLate:.1, soloSocial:.3, fixedFlexible:.7, directMultiStop:.5, classicUnusual:.4 } },
  { id: "zanzibar", countryCodes:["TZ"], weights: { seaMountain:-1, cityNature:.8, warmCold:-1, plannedAdventure:.4, luxuryBudget:.3, relaxActivity:-.8, cultureNightlife:.4, shortLong:.4, popularRemote:.5, comfortAuthentic:.7, foodCulture:.1, earlyLate:-.4, soloSocial:.1, fixedFlexible:.7, directMultiStop:.6, classicUnusual:.5 } },
  { id: "singapore", countryCodes:["SG"], weights: { seaMountain:-.3, cityNature:-.8, warmCold:-1, plannedAdventure:-.6, luxuryBudget:-.5, relaxActivity:.2, cultureNightlife:.2, shortLong:-.1, popularRemote:-.8, comfortAuthentic:-.8, foodCulture:-.7, earlyLate:.5, soloSocial:.3, fixedFlexible:-.2, directMultiStop:-.7, classicUnusual:-.1 } },
  { id: "seoul", countryCodes:["KR"], weights: { seaMountain:.1, cityNature:-.9, warmCold:0, plannedAdventure:.1, luxuryBudget:.1, relaxActivity:.6, cultureNightlife:.4, shortLong:.4, popularRemote:-.6, comfortAuthentic:.1, foodCulture:-.5, earlyLate:.8, soloSocial:.5, fixedFlexible:.2, directMultiStop:.1, classicUnusual:.2 } },
  { id: "vietnam", countryCodes:["VN"], weights: { seaMountain:-.4, cityNature:.5, warmCold:-.9, plannedAdventure:.8, luxuryBudget:1, relaxActivity:.3, cultureNightlife:-.2, shortLong:.8, popularRemote:.3, comfortAuthentic:.9, foodCulture:-.8, earlyLate:-.1, soloSocial:.5, fixedFlexible:.9, directMultiStop:.8, classicUnusual:.6 } },
  { id: "sriLanka", countryCodes:["LK"], weights: { seaMountain:-.4, cityNature:.8, warmCold:-1, plannedAdventure:.8, luxuryBudget:.7, relaxActivity:.1, cultureNightlife:-.3, shortLong:.7, popularRemote:.5, comfortAuthentic:.9, foodCulture:-.4, earlyLate:-.4, soloSocial:.2, fixedFlexible:.8, directMultiStop:.7, classicUnusual:.6 } },
  { id: "nepal", countryCodes:["NP"], weights: { seaMountain:1, cityNature:1, warmCold:.5, plannedAdventure:1, luxuryBudget:.8, relaxActivity:1, cultureNightlife:-.3, shortLong:.7, popularRemote:.8, comfortAuthentic:1, foodCulture:.2, earlyLate:-.9, soloSocial:-.1, fixedFlexible:.7, directMultiStop:.8, classicUnusual:.8 } },
  { id: "patagonia", countryCodes:["AR","CL"], weights: { seaMountain:.9, cityNature:1, warmCold:.8, plannedAdventure:1, luxuryBudget:.2, relaxActivity:.9, cultureNightlife:.7, shortLong:.9, popularRemote:.9, comfortAuthentic:.6, foodCulture:.6, earlyLate:-.9, soloSocial:-.2, fixedFlexible:.4, directMultiStop:.9, classicUnusual:.8 } },
  { id: "peru", countryCodes:["PE"], weights: { seaMountain:.7, cityNature:.7, warmCold:.1, plannedAdventure:.8, luxuryBudget:.6, relaxActivity:.7, cultureNightlife:-.8, shortLong:.7, popularRemote:.3, comfortAuthentic:.9, foodCulture:-.6, earlyLate:-.5, soloSocial:.2, fixedFlexible:.7, directMultiStop:.8, classicUnusual:.5 } },
  { id: "mexico", countryCodes:["MX"], weights: { seaMountain:-.5, cityNature:.2, warmCold:-.9, plannedAdventure:.5, luxuryBudget:.5, relaxActivity:.1, cultureNightlife:-.4, shortLong:.5, popularRemote:-.1, comfortAuthentic:.7, foodCulture:-.9, earlyLate:.4, soloSocial:.6, fixedFlexible:.8, directMultiStop:.5, classicUnusual:.2 } },
  { id: "canadaRockies", countryCodes:["CA"], weights: { seaMountain:.9, cityNature:1, warmCold:.8, plannedAdventure:.7, luxuryBudget:-.1, relaxActivity:.8, cultureNightlife:.8, shortLong:.6, popularRemote:.6, comfortAuthentic:.3, foodCulture:.5, earlyLate:-.8, soloSocial:-.1, fixedFlexible:.3, directMultiStop:.6, classicUnusual:.5 } },
  { id: "newZealand", countryCodes:["NZ"], weights: { seaMountain:.6, cityNature:1, warmCold:.2, plannedAdventure:1, luxuryBudget:.1, relaxActivity:.9, cultureNightlife:.7, shortLong:1, popularRemote:.8, comfortAuthentic:.5, foodCulture:.5, earlyLate:-.6, soloSocial:.1, fixedFlexible:.8, directMultiStop:1, classicUnusual:.8 } }
];

export const DESTINATION_CATALOG_FALLBACK_COUNT = DEFAULT_DESTINATIONS.length;
let destinationCatalogCache: { expiresAt: number; value: Destination[] } | null = null;

function normalizeRemoteDestination(id: string, value: unknown): Destination | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as { active?: unknown; label?: unknown; countryCodes?: unknown; weights?: unknown };
  if (raw.active === false || !raw.weights || typeof raw.weights !== "object") return null;
  const weights: Partial<Record<QuizAxis, number>> = {};
  for (const axis of QUIZ_AXES) {
    const numeric = Number((raw.weights as Record<string, unknown>)[axis]);
    if (Number.isFinite(numeric)) weights[axis] = Math.max(-1, Math.min(1, numeric));
  }
  if (!Object.keys(weights).length) return null;
  const countryCodes = Array.isArray(raw.countryCodes)
    ? raw.countryCodes.map((code) => String(code || "").trim().toUpperCase()).filter((code) => /^[A-Z]{2}$/.test(code))
    : [];
  return {
    id,
    countryCodes,
    label: typeof raw.label === "string" && raw.label.trim() ? raw.label.trim() : undefined,
    weights
  };
}

export async function loadDestinationCatalog(maxEntries = 250): Promise<Destination[]> {
  if (destinationCatalogCache && destinationCatalogCache.expiresAt > Date.now()) return destinationCatalogCache.value;
  const merged = new Map(DEFAULT_DESTINATIONS.map((item) => [item.id, item]));
  try {
    const snapshot = await getDocs(query(collection(db, "destinationCatalog"), limit(Math.max(1, Math.min(500, maxEntries)))));
    snapshot.docs.forEach((entry) => {
      const normalized = normalizeRemoteDestination(entry.id, entry.data());
      if (normalized) merged.set(normalized.id, normalized);
    });
  } catch {
    // The built-in catalog keeps recommendations available if the remote catalog is unavailable.
  }
  const value = Array.from(merged.values());
  destinationCatalogCache = { expiresAt: Date.now() + 15 * 60 * 1000, value };
  return value;
}

function rankCatalog(profile: TravelerQuizProfile, catalog: Destination[], options: DestinationRankingOptions = {}): DestinationMatch[] {
  const answers = new Map(profile.answers.map((item) => [item.axis, item.choice]));
  if (!answers.size) return [];
  const excluded = new Set((options.excludeCountryCodes || []).map((code) => String(code || "").trim().toUpperCase()));
  const ranked = catalog.flatMap((destination) => {
    if (destination.countryCodes.some((code) => excluded.has(code))) return [];
    let total = 0;
    let weightedAnswers = 0;
    answers.forEach((choice, axis) => {
      const weight = destination.weights[axis];
      if (typeof weight !== "number") return;
      total += 1 - Math.abs(choice - weight) / 2;
      weightedAnswers += 1;
    });
    if (!weightedAnswers) return [];
    return [{
      id: destination.id,
      score: Math.round(total / weightedAnswers * 100),
      countryCodes: [...destination.countryCodes],
      label: destination.label
    }];
  }).sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  return ranked.slice(0, Math.max(1, Math.min(25, options.limit || 10)));
}

export function rankDestinations(profile: TravelerQuizProfile, options: DestinationRankingOptions = {}): DestinationMatch[] {
  return rankCatalog(profile, DEFAULT_DESTINATIONS, options);
}

export async function rankDestinationCatalog(profile: TravelerQuizProfile, options: DestinationRankingOptions = {}): Promise<DestinationMatch[]> {
  return rankCatalog(profile, await loadDestinationCatalog(), options);
}

export async function buildTravelerKnowledgeContext(uid: string, focus = ""): Promise<string> {
  const personal = loadLocalQuiz(uid);
  const [community, communityReviews, destinationCatalog] = await Promise.all([
    loadCommunityQuizSummary(),
    loadCommunityReviewInsights(250, 24, 18, focus),
    loadDestinationCatalog()
  ]);
  const topDestinations = rankCatalog(personal, destinationCatalog, { limit: 10 });
  return JSON.stringify({
    travelerPreferences: {
      source: "traveler quiz",
      answers: personal.answers.map(({ axis, choice }) => ({ axis, choice })),
      topDestinationMatches: topDestinations
    },
    communityTravelerPreferences: {
      source: "aggregated Mundely traveler quiz responses; use only as an opinion signal, never as an objective fact",
      sampledUsers: community.users,
      sampledResponses: community.responses,
      axes: community.axes
    },
    communityReviews: {
      ...communityReviews,
      instruction: "Use these public reviews only as subjective community evidence. Prefer repeated patterns and reviews with more helpful votes, mention when evidence is sparse, and never turn a community opinion into an objective fact."
    }
  });
}
